import {
  createEntityAdapter,
  createSelector,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";
import { toast } from "react-toastify";

import { getUserFriendlyErrorMessage } from "api/UserFriendlyError";
import { getCollectionMedia, getCollectionTags } from "api/media-library";
import { CollectionFile } from "shared/types/files";
import { ReduxRootState } from "@/types/redux-store";

import {
  FileToUpload,
  startUploadsToCollection,
  UploadingMode,
} from "./uploading-files.model";
import { resetCollectionsTags } from "./components/collection-media/reset-collection-tags";

const featureKey = "mediaPickerFiles" as const;

const mediaFilesAdapter = createEntityAdapter<CollectionFile, string>({
  selectId: (file) => file._id,
});

const mediaFilesSlice = createSlice({
  name: featureKey,
  initialState: mediaFilesAdapter.getInitialState(),
  reducers: {
    setFiles(state, action: PayloadAction<CollectionFile[]>) {
      mediaFilesAdapter.upsertMany(state, action.payload);
    },
    upsertFile(state, action: PayloadAction<CollectionFile>) {
      mediaFilesAdapter.upsertOne(state, action.payload);
    },
    clearFiles(state) {
      mediaFilesAdapter.removeAll(state);
    },
  },
});

export const collectionMediaFilesReducer = mediaFilesSlice.reducer;
export const collectionMediaFilesActions = mediaFilesSlice.actions;
export type CollectionMediaFilesState = ReturnType<
  typeof collectionMediaFilesReducer
>;

function getMediaFilesState(state: ReduxRootState) {
  return (
    (
      state as ReduxRootState & {
        [featureKey]?: ReturnType<typeof collectionMediaFilesReducer>;
      }
    )[featureKey] ?? mediaFilesAdapter.getInitialState()
  );
}

const adapterSelectors = mediaFilesAdapter.getSelectors(getMediaFilesState);

export const selectCollectionMediaFileById = adapterSelectors.selectById;
export const selectAllCollectionMediaFiles = adapterSelectors.selectAll;

export const selectCollectionMediaFilesByIds = createSelector(
  selectAllCollectionMediaFiles,
  (_state: ReduxRootState, ids: string[]) => ids,
  (files, ids) =>
    ids
      .map((id) => files.find((file) => file._id === id) ?? null)
      .filter(Boolean) as CollectionFile[]
);

export async function fetchCollectionMediaFiles(params: {
  collectionId: string;
  page: number;
  limit: number;
  name?: string;
  description?: string;
  sourceName?: string;
  favorite?: boolean;
  mediaType?: "image" | "video" | null;
  usage?: "used" | "unused" | null;
  tags?: string[];
  excludeAccounts?: string[];
  sort?: string;
}) {
  try {
    return await getCollectionMedia(params);
  } catch (error) {
    toast.error(getUserFriendlyErrorMessage(error));
    throw error;
  }
}

export async function fetchCollectionTags(collectionId: string) {
  try {
    return await getCollectionTags(collectionId);
  } catch (error) {
    toast.error(getUserFriendlyErrorMessage(error));
    throw error;
  }
}

export function uploadFilesToCollection({
  files,
  collectionId,
  onFileUploaded,
  uploadingMode,
  batchId,
}: {
  files: FileToUpload[];
  collectionId: string;
  onFileUploaded?: (file: CollectionFile, slot: number) => void;
  uploadingMode: UploadingMode;
  batchId?: string;
}) {
  return async (dispatch: (action: unknown) => unknown) => {
    return await dispatch(
      startUploadsToCollection({
        collectionId,
        files,
        uploadingMode,
        batchId,
        onFileUploaded: (file, slot) => {
          dispatch(collectionMediaFilesActions.upsertFile(file));
          onFileUploaded?.(file, slot);
          dispatch(resetCollectionsTags());
        },
      }) as never
    );
  };
}
